/**
 * Dashboard section card — a numbered eyebrow, a title, and a divided list.
 *
 * Public interface: `number` + `title` + `children`. Hides the header layout
 * and the row-divider chrome so every section on a page matches without each
 * one rebuilding it.
 *
 * The number is positional (it reflects where the card sits on the page, not
 * what it contains), so the page passes it rather than the card owning it.
 *
 * Props:
 *   number    — eyebrow shown before the title, e.g. '02'; omit to hide it
 *   title     — section heading
 *   children  — rows; use <SectionCardList> for a divided list
 *   className — extra class(es) on the card
 *
 * All other props pass through to the root <section>.
 */
export default function SectionCard({ number, title, children, className = '', ...props }) {
  const classes = ['section-card', className].filter(Boolean).join(' ');

  return (
    <section className={classes} {...props}>
      <header className="section-card-header">
        {number != null && <span className="section-card-number" aria-hidden="true">{number}</span>}
        <h2 className="section-card-title">{title}</h2>
      </header>
      {children}
    </section>
  );
}

/** Divided row list for use inside a SectionCard. */
export function SectionCardList({ children, className = '', ...props }) {
  return (
    <ul className={['section-card-list', className].filter(Boolean).join(' ')} {...props}>
      {children}
    </ul>
  );
}

/** Empty-state line for a section with nothing to show. */
export function SectionCardEmpty({ children }) {
  return <p className="section-card-empty">{children}</p>;
}
